<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div class="sm:flex sm:items-center sm:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Subscription Plans</h1>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Manage subscription plans, pricing, and features for your platform
        </p>
      </div>
      <div class="mt-4 sm:mt-0">
        <Link
          :href="route('site-owner.subscriptions.plans.create')"
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
        >
          <PlusIcon class="-ml-1 mr-2 h-5 w-5" />
          Create New Plan
        </Link>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label for="status" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Status Filter
            </label>
            <select
              id="status"
              v-model="filters.status"
              @change="applyFilters"
              class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Plans</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Plans Table -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Plan Name
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Pricing
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Trial Days
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Subscribers
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Stripe
              </th>
              <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="plan in plans" :key="plan.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div>
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ plan.name }}
                    </div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">
                      {{ plan.slug }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-white">
                  <div v-if="plan.is_free" class="font-semibold text-green-600 dark:text-green-400">
                    FREE
                  </div>
                  <div v-else>
                    <div class="font-semibold">£{{ plan.price_monthly }}/mo</div>
                    <div v-if="plan.price_annual" class="text-xs text-gray-500 dark:text-gray-400">
                      £{{ plan.price_annual }}/yr
                      <span v-if="plan.annual_savings_percentage" class="text-green-600 dark:text-green-400">
                        (Save {{ plan.annual_savings_percentage }}%)
                      </span>
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                {{ plan.trial_days }} days
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="[
                    plan.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
                    'px-2 inline-flex text-xs leading-5 font-semibold rounded-full'
                  ]"
                >
                  {{ plan.is_active ? 'Active' : 'Inactive' }}
                </span>
                <span
                  v-if="plan.is_public"
                  class="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                >
                  Public
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                <div class="flex items-center">
                  <UsersIcon class="h-4 w-4 mr-1 text-gray-400" />
                  {{ plan.subscriptions_count }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  v-if="plan.has_stripe_integration"
                  class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                >
                  <CheckCircleIcon class="h-3 w-3 mr-1" />
                  Configured
                </span>
                <span
                  v-else
                  class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                >
                  <ExclamationTriangleIcon class="h-3 w-3 mr-1" />
                  Not Setup
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div class="flex items-center justify-end space-x-2">
                  <Link
                    :href="route('site-owner.subscriptions.plans.show', plan.id)"
                    class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                    title="View Plan"
                  >
                    <EyeIcon class="h-5 w-5" />
                  </Link>
                  <Link
                    :href="route('site-owner.subscriptions.plans.edit', plan.id)"
                    class="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                    title="Edit Plan"
                  >
                    <PencilIcon class="h-5 w-5" />
                  </Link>
                  <button
                    @click="duplicatePlan(plan.id)"
                    class="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                    title="Duplicate Plan"
                  >
                    <DocumentDuplicateIcon class="h-5 w-5" />
                  </button>
                  <button
                    @click="togglePlan(plan.id)"
                    :class="[
                      plan.is_active
                        ? 'text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300'
                        : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                    ]"
                    :title="plan.is_active ? 'Deactivate Plan' : 'Activate Plan'"
                  >
                    <component :is="plan.is_active ? ArrowPathIcon : CheckIcon" class="h-5 w-5" />
                  </button>
                  <button
                    v-if="plan.subscriptions_count === 0"
                    @click="deletePlan(plan)"
                    class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    title="Delete Plan"
                  >
                    <TrashIcon class="h-5 w-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-if="plans.length === 0" class="text-center py-12">
        <RectangleStackIcon class="mx-auto h-12 w-12 text-gray-400" />
        <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No plans found</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Get started by creating a new subscription plan.
        </p>
        <div class="mt-6">
          <Link
            :href="route('site-owner.subscriptions.plans.create')"
            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon class="-ml-1 mr-2 h-5 w-5" />
            Create New Plan
          </Link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  UsersIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CheckIcon,
  RectangleStackIcon,
} from '@heroicons/vue/24/outline';
import route from '@/Utils/route';

const props = defineProps({
  plans: {
    type: Array,
    required: true,
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

const filters = ref({
  status: props.filters.status || '',
});

const applyFilters = () => {
  router.get(route('site-owner.subscriptions.plans.index'), filters.value, {
    preserveState: true,
    preserveScroll: true,
  });
};

const togglePlan = (planId) => {
  if (confirm('Are you sure you want to change this plan\'s status?')) {
    router.post(route('site-owner.subscriptions.plans.toggle', planId), {}, {
      preserveScroll: true,
    });
  }
};

const duplicatePlan = (planId) => {
  if (confirm('This will create a copy of the plan with all its features. Continue?')) {
    router.post(route('site-owner.subscriptions.plans.duplicate', planId));
  }
};

const deletePlan = (plan) => {
  if (confirm(`Are you sure you want to delete the "${plan.name}" plan? This action cannot be undone.`)) {
    router.delete(route('site-owner.subscriptions.plans.destroy', plan.id));
  }
};
</script>
